import { Fragment, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProviderHealth, type ProviderHealthResponse } from '../api/endpoints.js';
import { useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  PageHead,
  StatCard,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import '../styles/providerhealth.mobile.css';

const DEFAULT_PROVIDERS = ['mailcom', 'hotmail', 'gmail'] as const;

export function ProviderHealthPage(): JSX.Element {
  const { providerId: routeId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState(routeId ?? '');
  const providerId = routeId ?? '';
  const canView = useCan('provider:view_health');

  const query = useApiQuery<ProviderHealthResponse>(
    (options) => getProviderHealth(options, providerId),
    [providerId],
  );

  function onSubmit(ev: FormEvent): void {
    ev.preventDefault();
    const trimmed = search.trim();
    if (!trimmed) return;
    navigate(`/providers/${encodeURIComponent(trimmed)}`);
  }

  return (
    <div className="ph-page">
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Sağlayıcı Sağlığı</>}
        title="Sağlayıcı Sağlığı"
        subtitle="Kapasite ve son yenileme özeti. Ham parent / alias kayıtları API tarafından filtrelenir — bu ekran yalnızca operatör için güvenli alanlar gösterir. mail.com için detaylı yönetim sayfası ayrıca mevcuttur."
      />

      <Card>
        <h2 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600 }}>Sağlayıcı Seç</h2>
        <form onSubmit={onSubmit} aria-label="sağlayıcı seç">
          <div className="ph-select-row">
            <div className="ph-select-input">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="sağlayıcı id"
                aria-label="sağlayıcı id"
                list="provider-suggestions"
                iconLeft={<Icon.Globe size={14} />}
              />
              <datalist id="provider-suggestions">
                {DEFAULT_PROVIDERS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </div>
            <Button type="submit" variant="primary" icon={<Icon.ArrowUR size={14} />}>
              Aç
            </Button>
          </div>
        </form>
      </Card>

      {!providerId ? (
        <EmptyState
          label="Bir sağlayıcı id seçin."
          hint="mailcom üretimde aktif; hotmail/gmail rezerve edilmiş slotlardır."
          icon={<Icon.Globe size={22} />}
        />
      ) : null}
      {providerId && !canView ? (
        <ErrorState reason="capability_missing:provider:view_health" />
      ) : null}
      {providerId && canView && query.kind === 'loading' ? <LoadingState /> : null}
      {providerId && canView && query.kind === 'error' ? (
        <ErrorState reason={query.reason} onRetry={query.reload} />
      ) : null}
      {providerId && canView && query.kind === 'ok' ? (
        <ProviderDetail provider={query.data.provider} correlationId={query.data.correlationId} />
      ) : null}
    </div>
  );
}

function ProviderDetail({
  provider,
  correlationId,
}: {
  readonly provider: ProviderHealthResponse['provider'];
  readonly correlationId: string;
}): JSX.Element {
  const degraded = provider.degradationReason !== null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card flush>
        <CardHeader
          title={`Özet — ${provider.providerId}`}
          actions={
            degraded ? (
              <Badge tone="danger" dot>degraded</Badge>
            ) : (
              <Badge tone="success" dot>healthy</Badge>
            )
          }
        />
        <CardBody>
          {degraded ? (
            <div style={{ marginBottom: 12 }}>
              <Banner tone="danger">
                <strong>Degradation reason:</strong> {provider.degradationReason}
              </Banner>
            </div>
          ) : null}

          <div className="kpi-grid kpi-grid-legacy">
            <StatCard
              label="Satılabilir alias (tahmini)"
              value={statNumber(provider.sellableAliasEstimate)}
              testId="ph-sellable"
            />
            <StatCard
              label="Toplam parent"
              value={statNumber(provider.totalParents)}
              testId="ph-parents"
            />
            <StatCard
              label="Son yenileme denemeleri"
              value={statNumber(provider.recentRenewalAttempts)}
              testId="ph-renewals"
            />
            <StatCard
              label="Son yenileme hataları"
              value={statNumber(provider.recentRenewalFailures)}
              trend={provider.recentRenewalFailures > 0 ? 'down' : 'flat'}
              meta={provider.recentRenewalFailures > 0 ? 'Detay için runbook' : 'Hata yok'}
              testId="ph-failures"
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="dl-rows">
              <div className="dl-row">
                <div className="dl-key">Gözlem zamanı</div>
                <div className="dl-val t-mono t-xs">{provider.observedAt}</div>
              </div>
              <div className="dl-row">
                <div className="dl-key">Correlation</div>
                <div className="dl-val t-mono t-xs">{correlationId}</div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }} className="dashboard-grid ph-status-grid">
        <Card flush>
          <CardHeader title="Parent'lar — duruma göre" />
          <CardBody tight style={{ padding: Object.keys(provider.parentsByStatus).length === 0 ? 20 : 0 }}>
            <StatusTable byStatus={provider.parentsByStatus} />
          </CardBody>
        </Card>
        <Card flush>
          <CardHeader title="Alias'lar — duruma göre" />
          <CardBody tight style={{ padding: Object.keys(provider.aliasesByStatus).length === 0 ? 20 : 0 }}>
            <StatusTable byStatus={provider.aliasesByStatus} />
          </CardBody>
        </Card>
      </div>

      <Card flush>
        <CardHeader title="Gözlenen bakım kapsamı" />
        <CardBody>
          <div className="dl-rows">
            <div className="dl-row">
              <div className="dl-key">Etkin</div>
              <div className="dl-val">
                {provider.maintenance.enabled ? (
                  <Badge tone="warn" dot>etkin</Badge>
                ) : (
                  <Badge tone="success" dot>kapalı</Badge>
                )}
              </div>
            </div>
            <div className="dl-row">
              <div className="dl-key">Scope</div>
              <div className="dl-val t-mono">{provider.maintenance.scope}</div>
            </div>
            <div className="dl-row">
              <div className="dl-key">Neden</div>
              <div className="dl-val">{provider.maintenance.reason ?? '—'}</div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * A stat-card count in tr-TR grouping ("4.827.361.000"). The shared KPI
 * sizing keeps values up to ~13 characters on one line on phones; a <wbr>
 * after each thousands separator lets a longer value wrap between digit
 * groups, never inside one. The text content is unchanged.
 */
function statNumber(n: number): ReactNode {
  const groups = n.toLocaleString('tr-TR').split('.');
  if (groups.length === 1) return groups[0];
  return groups.map((group, i) => (
    <Fragment key={i}>
      {group}
      {i < groups.length - 1 ? (
        <>
          .<wbr />
        </>
      ) : null}
    </Fragment>
  ));
}

/**
 * Status keys are long snake_case (occasionally dotted / colon-scoped)
 * tokens. A <wbr> after each "_", ".", ":" or "/" lets a key that does not
 * fit its column wrap at a word boundary on narrow screens instead of
 * mid-word ("-" already allows a break); the text content (and copy/paste)
 * is unchanged.
 */
function breakableKey(key: string): ReactNode {
  const parts = key.split(/([_.:/])/);
  if (parts.length === 1) return key;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Fragment key={i}>
        {part}
        <wbr />
      </Fragment>
    ) : (
      part
    ),
  );
}

function StatusTable({
  byStatus,
}: {
  readonly byStatus: Readonly<Record<string, number>>;
}): JSX.Element {
  const keys = Object.keys(byStatus);
  if (keys.length === 0) {
    return <EmptyState label="Kayıt yok." />;
  }
  return (
    <Table dense className="tbl-compact ph-status-tbl">
      <THead>
        <TRow>
          <TH>Durum</TH>
          <TH style={{ textAlign: 'right' }}>Adet</TH>
        </TRow>
      </THead>
      <TBody>
        {keys.map((k) => (
          <TRow key={k}>
            <TD className="t-mono cell-wrap ph-status-key">{breakableKey(k)}</TD>
            <TD className="t-mono t-num cell-nowrap ph-status-count" style={{ textAlign: 'right' }}>{(byStatus[k] ?? 0).toLocaleString('tr-TR')}</TD>
          </TRow>
        ))}
      </TBody>
    </Table>
  );
}
